import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  PhotoCameraOutlined as PhotoCameraIcon,
  SettingsOutlined as SettingsIcon,
  LogoutOutlined as LogoutIcon,
} from "@mui/icons-material";

import { useAuth } from "../../contexts/AuthContext";
import { useMessageContext } from "../../contexts/MessageContext";
import { uploadUserImage } from "../../services/TraineeService";
import { getErrorMessage } from "../../enums/ErrorMessages";
import { imageUrl } from "../../utils/imageUrl";

const ProfileMenu = () => {
  const { currentUser, refetchUser, logout } = useAuth();
  const { notificationHandler } = useMessageContext();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      await uploadUserImage(file);
      await refetchUser();
    } catch (error) {
      notificationHandler.error(getErrorMessage(error.response?.data?.errorCode));
    } finally {
      setLoading(false);
      closeMenu();
    }
  };

  const goToSettings = () => {
    closeMenu();
    navigate("/app/setting");
  };

  const handleLogout = () => {
    closeMenu();
    logout();
    navigate("/app/login");
  };

  const initial = currentUser?.name?.trim()?.[0] || "?";

  return (
    <>
      <IconButton
        onClick={openMenu}
        sx={{
          p: 0.25,
          width: 34,
          height: 34,
          border: "1px solid",
          borderColor: "neutral.100",
          backgroundColor: "background.paper",
          "&:hover": { borderColor: "neutral.400" },
        }}
      >
        <Box sx={{ position: "relative", width: 28, height: 28 }}>
          <Avatar
            src={imageUrl(currentUser?.photoUrl, 28) || undefined}
            sx={{
              width: 28,
              height: 28,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: "primary.surface",
              color: "primary.text",
            }}
          >
            {initial}
          </Avatar>
          {loading && (
            <CircularProgress
              size={28}
              thickness={3}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                color: "primary.main",
              }}
            />
          )}
        </Box>
      </IconButton>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { minWidth: 240, mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {currentUser?.name || ""}
          </Typography>
          {currentUser?.email && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {currentUser.email}
            </Typography>
          )}
        </Box>
        <Divider sx={{ borderColor: "neutral.100" }} />

        <MenuItem component="label" disabled={loading}>
          <ListItemIcon>
            <PhotoCameraIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          </ListItemIcon>
          החלף תמונת פרופיל
          <input
            type="file"
            hidden
            accept="image/png, image/gif, image/jpeg"
            onChange={handleFileUpload}
          />
        </MenuItem>

        <MenuItem onClick={goToSettings}>
          <ListItemIcon>
            <SettingsIcon sx={{ fontSize: 20, color: "text.secondary" }} />
          </ListItemIcon>
          הגדרות
        </MenuItem>

        <Divider sx={{ borderColor: "neutral.100" }} />

        <MenuItem onClick={handleLogout} sx={{ color: "semantic.danger" }}>
          <ListItemIcon>
            <LogoutIcon sx={{ fontSize: 20, color: "semantic.danger" }} />
          </ListItemIcon>
          התנתקות
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProfileMenu;
